import React, { useState, useEffect, useContext } from 'react';
import Modal from 'react-bootstrap/Modal';
import { Table, Spinner, Alert, Button } from 'react-bootstrap';
import SimpleBar from 'simplebar-react';
import 'simplebar/dist/simplebar.min.css';
import fetchApi from '../../api/fetchApi';
import './Dashboard.css';
import { AuthContext } from '../../context/AuthContext';
import { Permit } from '../../types/types';

interface ModalFormProps {
    isOpen: boolean;
    onClose: () => void;
    projectId: number;
    projectName: string;
}

const ModalTable: React.FC<ModalFormProps> = ({ isOpen, onClose, projectId, projectName }) => {
    const [tableData, setTableData] = useState<any[]>([]);
    const [displayedData, setDisplayedData] = useState<any[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [pagination, setPagination] = useState({
        page: 1,
        size: 4,
        numberPages: 1,
    });
    const authContext = useContext(AuthContext);

    useEffect(() => {
        if (isOpen) {
            fetchData();
        }
    }, [isOpen]);

    useEffect(() => {
        applyPagination();
    }, [tableData, pagination.page, pagination.size]);

    const fetchData = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetchApi({
                endpoint: `/close-months/project/${projectId}`,
                method: 'GET',
            });
            setTableData(response);
            setPagination(prev => ({
                ...prev,
                numberPages: Math.ceil(response.length / prev.size),
            }));
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Error desconocido');
        } finally {
            setLoading(false);
        }
    };

    const applyPagination = () => {
        const startIndex = (pagination.page - 1) * pagination.size;
        const paginatedData = tableData.slice(startIndex, startIndex + pagination.size);
        setDisplayedData(paginatedData);
    };

    const handlePaginationChange = (page: number) => {
        setPagination(prev => ({ ...prev, page }));
    };

    const hasPermission = (moduleName: string, actionName: string): boolean => {
        return authContext?.user?.permits?.some((permit: Permit) =>
            permit.moduleName === moduleName && permit.actionName === actionName
        ) ?? false;
    }; 

    if (!hasPermission("projectHistory", "read")) {
        return <div className="dashboard-container"><p>No tienes permiso para ver esta sección.</p></div>;
    }

    return (
        <Modal show={isOpen} onHide={onClose} size="xl" fullscreen="lg-down">
            <Modal.Header closeButton className="modal-header-custom">
                <Modal.Title className="white">Historial de cierre de mes del proyecto: {projectName}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {loading ? (
                    <div className="text-center">
                        <Spinner animation="border" />
                    </div>
                ) : error ? (
                    <Alert variant="danger">{error}</Alert>
                ) : (
                    <>
                        <SimpleBar style={{ maxHeight: '600px' }}>
                            <Table striped bordered hover className='text-center'>
                                <thead className="head_block ">
                                    <tr>
                                        <td className="head_t">EAC Revenue</td>
                                        <td className="head_t">EAC Cost</td>
                                        <td className="head_t">EAC Margin</td>
                                        <td className="head_t">Accumulated Revenue</td>
                                        <td className="head_t">Accumulated Cost</td>
                                        <td className="head_t">Accumulated Profit</td>
                                        <td className="head_t">Accumulated Margin</td>
                                        <td className="head_t">ETC Revenue</td>
                                        <td className="head_t">ETC Cost</td>
                                        <td className="head_t">ETC Profit</td>
                                        <td className="head_t">ETC Margin</td>
                                        <td className="head_t">YTD Revenue</td>
                                        <td className="head_t">YTD Cost</td>
                                        <td className="head_t">YTD Profit</td>
                                        <td className="head_t">YTD Margin</td>
                                        <td className="head_t">Date</td>
                                    </tr>
                                </thead>
                                <tbody>
                                    {displayedData.length > 0 ? (
                                        displayedData.map((row, index) => (
                                            <tr key={index}>
                                                <td>{row.eacRevenue}</td>
                                                <td>{row.eacCost}</td>
                                                <td>{row.eacMargin}</td>
                                                <td>{row.accumulatedRevenue}</td>
                                                <td>{row.accumulatedCost}</td>
                                                <td>{row.accumulatedProfit}</td>
                                                <td>{row.accumulatedMargin}</td>
                                                <td>{row.etcRevenue}</td>
                                                <td>{row.etcCost}</td>
                                                <td>{row.etcProfit}</td>
                                                <td>{row.etcMargin}</td>
                                                <td>{row.ytdRevenue}</td>
                                                <td>{row.ytdCost}</td>
                                                <td>{row.ytdProfit}</td>
                                                <td>{row.ytdMargin}</td>
                                                <td>{row.date}</td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={16} className="text-center">No hay datos disponibles</td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </SimpleBar>
                        {pagination.numberPages > 1 && (
                            <div className="custom-pagination text-center mt-3">
                                <Button
                                    variant="secondary"
                                    className="me-2"
                                    onClick={() => handlePaginationChange(pagination.page - 1)}
                                    disabled={pagination.page === 1}
                                >
                                    &lt;
                                </Button>
                                {Array.from({ length: pagination.numberPages }, (_, i) => i + 1).map(page => (
                                    <Button
                                        key={page}
                                        className={`custom-pagination-item ${pagination.page === page ? 'active' : ''}`}
                                        onClick={() => handlePaginationChange(page)}
                                    >
                                        {page}
                                    </Button>
                                ))}
                                <Button
                                    className="pagination-next"
                                    onClick={() => handlePaginationChange(pagination.page + 1)}
                                    disabled={pagination.page === pagination.numberPages}
                                >
                                    &gt;
                                </Button>
                            </div>
                        )}
                    </>
                )}
            </Modal.Body>
        </Modal>
    );
};

export default ModalTable;